import type { ElementType, ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type SectionHeadingVariant =
  | 'default' // "Nossa" (top) + "História" (italic yellow)
  | 'italic-only' // "Tecnologia" (italic yellow) only
  | 'with-highlight'; // italic word wrapped in a yellow background box

export type SectionHeadingAlign = 'left' | 'center';

export type SectionHeadingSize = 'sm' | 'md' | 'lg' | 'xl';

interface SectionHeadingProps {
  /** Small line above the main heading (e.g. "Nossa") — hidden in italic-only variant */
  top?: string;
  /** The italicized yellow word (e.g. "História") */
  italic: string;
  /** Optional subtitle under the heading */
  subtitle?: ReactNode;
  variant?: SectionHeadingVariant;
  align?: SectionHeadingAlign;
  size?: SectionHeadingSize;
  /** Semantic tag — default h2, pass h1 for page titles */
  as?: ElementType;
  className?: string;
  /** Optional leading text that appears after the italic word, e.g. "para você" */
  suffix?: string;
  /** Render the accent word in italic — defaults to true */
  isItalic?: boolean;
}

const sizeMap: Record<SectionHeadingSize, string> = {
  sm: 'text-3xl md:text-4xl',
  md: 'text-4xl md:text-5xl',
  lg: 'text-5xl md:text-6xl',
  xl: 'text-5xl md:text-7xl',
};

const alignMap: Record<SectionHeadingAlign, string> = {
  left: 'items-start text-left',
  center: 'items-center text-center',
};

/**
 * Two-line Figma heading pattern — regular purple top + bold italic yellow
 * bottom. Used on every inner page.
 *
 * Variants:
 * - `default`        → "Nossa" (purple) \n "História" (italic yellow)
 * - `italic-only`    → just "Tecnologia" (italic yellow)
 * - `with-highlight` → italic word wrapped in a solid yellow box (Home CTA style)
 */
export function SectionHeading({
  top,
  italic,
  subtitle,
  variant = 'default',
  align = 'left',
  size = 'lg',
  as: Tag = 'h2',
  className,
  suffix,
  isItalic = true,
}: SectionHeadingProps) {
  return (
    <div className={cn('flex flex-col gap-3', alignMap[align], className)}>
      <Tag
        className={cn(
          'heading-display',
          alignMap[align],
          sizeMap[size],
          'leading-[1.05]'
        )}
      >
        {variant !== 'italic-only' && top && (
          <span className="heading-display__top">{top}</span>
        )}

        {variant === 'with-highlight' ? (
          <span className={cn('heading-display__italic', !isItalic && 'not-italic')}>
            <span className={cn(
              'inline-block rounded-full bg-brand-yellow px-5 py-1 text-brand-purple',
              isItalic && 'italic'
            )}>
              {italic}
            </span>
            {suffix && <span className={cn(isItalic && 'italic')}> {suffix}</span>}
          </span>
        ) : (
          <span className={cn('heading-display__italic', !isItalic && 'not-italic')}>
            <span className={cn(isItalic && 'italic')}>
              {italic}
              {suffix && <> {suffix}</>}
            </span>
          </span>
        )}
      </Tag>
      {subtitle && (
        <p className="max-w-2xl pt-2 text-brand-purple/70 text-base md:text-lg">
          {subtitle}
        </p>
      )}
    </div>
  );
}
